/**
 * Document Diff and Patch Utilities
 * Provides utilities for tracking and applying changes to documents
 */

/**
 * Simple diff algorithm to identify changes between two text strings
 * Returns an array of changes with operations: 'insert', 'delete', 'equal'
 */
export const computeTextDiff = (oldText, newText) => {
  const changes = [];
  
  // Simple line-based diff
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  
  let i = 0;
  let j = 0;
  
  while (i < oldLines.length || j < newLines.length) {
    if (i >= oldLines.length) {
      // Remaining lines are insertions
      changes.push({
        type: 'insert',
        content: newLines[j],
        lineNumber: j,
      });
      j++;
    } else if (j >= newLines.length) {
      // Remaining lines are deletions
      changes.push({
        type: 'delete',
        content: oldLines[i],
        lineNumber: i,
      });
      i++;
    } else if (oldLines[i] === newLines[j]) {
      // Lines are equal
      changes.push({
        type: 'equal',
        content: oldLines[i],
        lineNumber: i,
      });
      i++;
      j++;
    } else {
      // Lines are different - mark as change
      changes.push({
        type: 'delete',
        content: oldLines[i],
        lineNumber: i,
      });
      changes.push({
        type: 'insert',
        content: newLines[j],
        lineNumber: j,
      });
      i++;
      j++;
    }
  }
  
  return changes;
};

/**
 * Apply a patch to a document
 */
export const applyPatch = (originalText, patch) => {
  if (patch.type === 'replace') {
    return patch.newContent;
  }
  
  if (patch.type === 'append') {
    return originalText + '\n' + patch.content;
  }
  
  if (patch.type === 'prepend') {
    return patch.content + '\n' + originalText;
  }
  
  if (patch.type === 'insert') {
    const lines = originalText.split('\n');
    lines.splice(patch.lineNumber, 0, patch.content);
    return lines.join('\n');
  }
  
  if (patch.type === 'delete') {
    const lines = originalText.split('\n');
    lines.splice(patch.lineNumber, 1);
    return lines.join('\n');
  }
  
  return originalText;
};

/**
 * Create a patch object from old and new content
 */
export const createPatch = (oldContent, newContent, metadata = {}) => {
  return {
    id: `patch_${Date.now()}`,
    timestamp: new Date().toISOString(),
    oldContent,
    newContent,
    diff: computeTextDiff(oldContent, newContent),
    metadata,
  };
};

/**
 * Merge multiple patches into one
 */
export const mergePatches = (patches) => {
  if (patches.length === 0) return null;
  if (patches.length === 1) return patches[0];
  
  let currentContent = patches[0].oldContent;
  
  patches.forEach(patch => {
    currentContent = patch.newContent;
  });
  
  return {
    id: `merged_${Date.now()}`,
    timestamp: new Date().toISOString(),
    oldContent: patches[0].oldContent,
    newContent: currentContent,
    diff: computeTextDiff(patches[0].oldContent, currentContent),
    patches: patches.map(p => p.id),
  };
};

/**
 * Validate if a patch can be applied to current content
 */
export const validatePatch = (currentContent, patch) => {
  try {
    // Check if old content matches
    if (patch.oldContent && patch.oldContent !== currentContent) {
      return {
        valid: false,
        reason: 'Base content has changed',
      };
    }
    
    return {
      valid: true,
    };
  } catch (error) {
    return {
      valid: false,
      reason: error.message,
    };
  }
};

/**
 * Revert a patch
 */
export const revertPatch = (currentContent, patch) => {
  return patch.oldContent;
};

/**
 * Get a summary of changes in a patch
 */
export const getPatchSummary = (patch) => {
  if (!patch.diff) return 'No changes';
  
  const insertions = patch.diff.filter(d => d.type === 'insert').length;
  const deletions = patch.diff.filter(d => d.type === 'delete').length;
  const unchanged = patch.diff.filter(d => d.type === 'equal').length;
  
  return {
    insertions,
    deletions,
    unchanged,
    total: patch.diff.length,
  };
};

/**
 * Highlight changes in a diff for display
 */
export const highlightDiff = (diff) => {
  return diff.map(change => ({
    ...change,
    className: change.type === 'insert' ? 'diff-insert' :
               change.type === 'delete' ? 'diff-delete' :
               'diff-equal',
  }));
};

export default {
  computeTextDiff,
  applyPatch,
  createPatch,
  mergePatches,
  validatePatch,
  revertPatch,
  getPatchSummary,
  highlightDiff,
};

